import clsx from "clsx";
import { useState } from "react";
import { router, usePage } from "@inertiajs/react";
import logoFull from "@/assets/brand/logo-full-script.webp";
import globe from "@/assets/brand/globe.png";
import { Input } from "@/components/ui/Field";
import Button, { ButtonLink } from "@/components/ui/Button";
import type { PublicProps } from "@/types";
import type { SatelliteEvent } from "@/interfaces/satellite_event";

import table from "@/assets/photos/event-table.webp";
import cafe from "@/assets/photos/event-cafe.webp";
import drinks from "@/assets/photos/event-drinks.webp";
import venue from "@/assets/photos/event-venue.webp";
import city from "@/assets/photos/event-city.webp";
import outdoors from "@/assets/photos/event-outdoors.webp";

const MAX_PHOTOS = 6;

// Shown until the organizer uploads their own. Hack Clubbers round a table with
// drinks rather than the travel and landscape shots - the panel exists to answer
// "what would it look like if I turned up", and a city with no photos yet still
// has to answer that. Six of them, so the fallback fills the same grid a full
// set of the organizer's own would.
const STOCK = [table, cafe, drinks, venue, city, outdoors].map((url) => ({
  url,
  caption: null,
}));

// The city page's opening: the Figma's per-city hero. Same shape as the
// landing page's, with the city's name under the wordmark, this city's date on
// the meta line, and an email field that hands off to this city's signup form
// instead of the city picker.
export default function CityHero({
  event,
  photos,
  signupHref,
  rsvpsOpen,
  going,
  ageRange,
  photosTitle,
  highlightPhotos = false,
}: {
  event: SatelliteEvent;
  photos: { url: string; caption: string | null }[];
  signupHref: string;
  rsvpsOpen: boolean;
  going: boolean;
  ageRange: { min: number; max: number };
  // The organizer's own label for the photo wall, when they wrote one.
  photosTitle?: string | null;
  highlightPhotos?: boolean;
}) {
  const { props } = usePage<PublicProps>();
  const program = props.program;
  const [email, setEmail] = useState("");

  const shown = (photos.length > 0 ? photos : STOCK).slice(0, MAX_PHOTOS);

  // Only what we can honestly fill in. A city with no date yet says nothing
  // about one rather than printing a placeholder.
  const meta = [
    event.duration_label ?? "3-5 hours",
    `for teens ${ageRange.min}-${ageRange.max}`,
    event.date_stamp,
  ].filter(Boolean);

  const closed = event.past || !rsvpsOpen || event.full;

  return (
    <section className="border-blue-dianne/20 relative overflow-hidden border-b-2">
      {/* Pale sky, faint grid, and a globe wash - the Figma background stack. */}
      <div aria-hidden className="bg-band-hero absolute inset-0" />
      <div
        aria-hidden
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(39,78,99,.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(39,78,99,.07) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />
      <img
        src={globe}
        alt=""
        aria-hidden
        className="pointer-events-none absolute -top-40 -left-56 w-[52rem] max-w-none opacity-10"
      />

      <div className="relative mx-auto grid w-full max-w-6xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:py-16">
        <div className="border-blue-dianne/60 shadow-card flex flex-col gap-5 border bg-[rgba(255,253,246,0.82)] p-6 backdrop-blur-sm sm:p-8">
          {/* The Figma hangs the city's name off the bottom right of the
              wordmark. Stacked rather than positioned over it, so "Santo
              Domingo" doesn't run off the edge of the card. */}
          <div className="flex max-w-sm flex-col self-start">
            <img src={logoFull} alt={program.name} className="w-full" />
            <span className="font-display text-title text-ink-muted -mt-2 self-end text-right font-bold italic drop-shadow-[0_2px_0_rgba(255,255,255,0.85)]">
              {event.city ?? event.name}
            </span>
          </div>

          {/* The Figma underlines the final clause. Split on the last comma so
              the emphasis follows whatever the tagline says. */}
          <p className="font-display text-heading text-link font-bold">
            {(() => {
              const tagline =
                program.tagline ??
                "Make a website, hang out with friends, get free boba!";
              const split = tagline.lastIndexOf(", ");
              if (split === -1) return tagline;
              return (
                <>
                  {tagline.slice(0, split + 2)}
                  <span className="underline decoration-2">
                    {tagline.slice(split + 2)}
                  </span>
                </>
              );
            })()}
          </p>

          <p className="text-body text-ink-muted">{meta.join(" • ")}</p>

          <hr className="border-blue-dianne/30" />

          {going ? (
            <>
              <p className="font-display text-subheading text-ink font-extrabold italic">
                You're on the list!
              </p>
              <p className="text-body text-ink-muted">
                See you in {event.city ?? event.name}. Everything you need to
                know is further down this page.
              </p>
            </>
          ) : closed ? (
            <>
              <p className="font-display text-subheading text-ink font-extrabold italic">
                {event.past
                  ? "This one already happened"
                  : event.full
                    ? "This one is full"
                    : "Sign-ups open soon"}
              </p>
              <p className="text-body text-ink-muted">
                {event.past
                  ? "Have a look at the other cities - there may still be one near you."
                  : event.full
                    ? "Every spot is taken. Other cities may still have room."
                    : "This city is confirmed, the sign-up form just isn't open yet. Check back in a few days."}
              </p>
              <ButtonLink href="/events" variant="brand" size="lg">
                see other cities
              </ButtonLink>
            </>
          ) : (
            <>
              <p className="font-display text-subheading text-ink font-extrabold italic">
                SIGN UP for {program.name} {event.city ?? event.name}:
              </p>

              {/* Hands the address straight to this city's form, so the only
                  thing left to do there is the rest of the fields. */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  router.get(signupHref, { email });
                }}
                className="flex flex-col gap-2 sm:flex-row"
              >
                <label htmlFor="city-email" className="sr-only">
                  Your email address
                </label>
                <Input
                  id="city-email"
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  autoComplete="email"
                  className="flex-1"
                />
                <Button type="submit" className="shrink-0">
                  sign up!
                </Button>
              </form>

              <p className="text-caption text-ink-soft">
                Takes about a minute, and you don't need an account.
              </p>
            </>
          )}
        </div>

        {/* "what your event could look like" window */}
        <div
          data-slot="gallery"
          className={clsx(
            "border-blue-dianne/60 shadow-card flex flex-col border bg-[rgba(255,255,255,0.7)] backdrop-blur-sm",
            highlightPhotos && "ring-sunset-orange ring-4 ring-offset-2",
          )}
        >
          <div className="border-blue-dianne/60 flex items-center gap-3 border-b bg-[rgba(253,216,104,0.65)] px-3 py-1.5">
            <span className="font-display text-caption text-ink min-w-0 flex-1 truncate font-bold">
              {photosTitle || "what your event could look like..."}
            </span>
            {/* Decorative window chrome, not a real control. */}
            <span
              aria-hidden
              className="border-line text-ink font-display text-caption flex h-5 w-5 items-center justify-center border bg-[rgba(253,83,56,0.5)] leading-none"
            >
              x
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 p-3">
            {shown.map((photo) => (
              <img
                key={photo.url}
                src={photo.url}
                alt={photo.caption ?? ""}
                loading="lazy"
                className="border-blue-dianne/50 h-40 w-full border object-cover sm:h-64"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
